import type {
  RuleEvaluationResult,
  RuleViolation,
} from '@billing/validation/rules';
import type { UseFormReturnType } from '@mantine/form';
import type { Claim } from 'interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  extractRuleFailureMessages,
  extractRuleViolationsFromError,
  formatRuleViolationMessages,
} from 'utils/ruleUtils';
import { useRuleViolationFormErrors } from './useRuleViolationFormErrors';

interface LiveClaimRuleEvaluationState {
  evaluation: RuleEvaluationResult | null;
  error: Error | null;
  isChecking: boolean;
  isStale: boolean;
}

type RuleValidationForm = Pick<
  UseFormReturnType<Claim>,
  'clearFieldError' | 'errors' | 'setFieldError'
>;

export interface ClaimRuleValidationState {
  clearServerRuleValidation: () => void;
  hasCurrentBlockingRules: boolean;
  hasCurrentLiveBlockingRules: boolean;
  liveBlockingRuleMessages: string[];
  liveRuleError: Error | null;
  liveRuleWarningMessages: string[];
  isEvaluating: boolean;
  serverRuleErrors: string[];
  setServerRuleFailure: (error: unknown) => boolean;
}

const EMPTY_VIOLATIONS: RuleViolation[] = [];

export const useClaimRuleValidation = (
  form: RuleValidationForm,
  liveRules?: LiveClaimRuleEvaluationState,
  allowIncompleteSave = false
): ClaimRuleValidationState => {
  const [serverRuleErrors, setServerRuleErrors] = useState<string[]>([]);
  const [serverRuleViolations, setServerRuleViolations] = useState<
    RuleViolation[]
  >([]);
  const liveRuleViolations =
    liveRules?.evaluation?.violations ?? EMPTY_VIOLATIONS;
  const visibleRuleViolations = useMemo(
    () => [...liveRuleViolations, ...serverRuleViolations],
    [liveRuleViolations, serverRuleViolations]
  );
  const liveBlockingRuleMessages = useMemo(
    () => formatRuleViolationMessages(visibleRuleViolations, 'block'),
    [visibleRuleViolations]
  );
  const currentLiveBlockingRuleMessages = useMemo(
    () => formatRuleViolationMessages(liveRuleViolations, 'block'),
    [liveRuleViolations]
  );
  const liveRuleWarningMessages = useMemo(
    () => formatRuleViolationMessages(liveRuleViolations, 'warning'),
    [liveRuleViolations]
  );
  const hasCurrentBlockingRules =
    !allowIncompleteSave &&
    !liveRules?.isStale &&
    liveBlockingRuleMessages.length > 0;
  const hasCurrentLiveBlockingRules =
    !allowIncompleteSave &&
    !liveRules?.isStale &&
    currentLiveBlockingRuleMessages.length > 0;
  const isEvaluating = Boolean(
    !allowIncompleteSave &&
      liveRules &&
      (liveRules.isChecking || liveRules.isStale)
  );

  const clearServerRuleValidation = useCallback(() => {
    setServerRuleErrors([]);
    setServerRuleViolations([]);
  }, []);

  const setServerRuleFailure = useCallback((error: unknown): boolean => {
    const messages = extractRuleFailureMessages(error);
    if (messages.length === 0) return false;

    setServerRuleErrors(messages);
    setServerRuleViolations(extractRuleViolationsFromError(error));
    return true;
  }, []);

  useRuleViolationFormErrors(
    form,
    allowIncompleteSave ? EMPTY_VIOLATIONS : visibleRuleViolations
  );

  useEffect(() => {
    if (liveRules?.isStale) clearServerRuleValidation();
  }, [clearServerRuleValidation, liveRules?.isStale]);

  return {
    clearServerRuleValidation,
    hasCurrentBlockingRules,
    hasCurrentLiveBlockingRules,
    liveBlockingRuleMessages,
    liveRuleError: liveRules?.error ?? null,
    liveRuleWarningMessages,
    isEvaluating,
    serverRuleErrors,
    setServerRuleFailure,
  };
};
